import Close from '@/SVG/Close'
import Button from '@/components/Button'
import { useUserPrefs } from '@/context/UserPrefsContext'
import type Hotspot from '@/types/Hotspot'
import classnames from 'classnames'
import { useCallback, useEffect } from 'react'

export type HotspotCardProps = {
  data: Hotspot
  onClose?: () => void
  onAnimationComplete?: () => void
  isClosing?: boolean
  isOpening?: boolean
}

export default function HotspotCard({
  data,
  onClose,
  onAnimationComplete,
  isClosing = false,
  isOpening = false,
}: HotspotCardProps) {
  // Animation duration in ms - should match the CSS duration
  const animationDuration = 500
  const { copyHelper } = useUserPrefs()

  const handleChapterChange = useCallback(() => {
    if (onClose) {
      console.log('Chapter change detected, closing hotspot card', 'navigation')
      onClose()
    }
  }, [onClose])

  useEffect(() => {
    window.addEventListener('chapter:change', handleChapterChange)

    return () => {
      window.removeEventListener('chapter:change', handleChapterChange)
    }
  }, [handleChapterChange])

  // Delay the entrance animation when a previous card is closing
  const entranceDelay = isOpening ? 'delay-500' : ''

  // Set different z-index values for opening and closing cards to prevent visual overlap
  const zIndexClass = isClosing ? 'z-30' : 'z-40'

  // Position hotspot cards absolutely to prevent layout shifts
  const wrapperClassName = classnames(
    `${zIndexClass} w-screen h-screen bg-black bg-opacity-60 backdrop-blur-xs flex items-center justify-center`,
    'transition-all duration-500',
    'fixed top-0 left-0 md:rounded-none md:shadow-xl',
    'pointer-events-auto', // Card intercepts pointer events to prevent canvas interaction
    {
      [`animate-slide-in-left ${entranceDelay}`]: !isClosing,
      'animate-slide-out-left': isClosing,
      'md:w-[462px] ': !data.copy2,
      'md:w-[609px]': data.copy2,
    }
  )

  const parseHotspotCopy = (copy: string, keyPrefix = 'copy') => {
    return copy.split('\n').map((paragraph, index) => (
      <span key={`${data.id}-${keyPrefix}-${index}`}>
        {paragraph}
        <br />
      </span>
    ))
  }

  // Retrieve main and secondary copy based on the user's set language
  const mainCopy = parseHotspotCopy(copyHelper(data.copy, data.ma), 'copy')
  const secondaryCopy = data.copy2 ? parseHotspotCopy(copyHelper(data.copy2, data.ma2), 'copy2') : null

  // Wait for animation to complete before calling onAnimationComplete
  useEffect(() => {
    if (isClosing && onAnimationComplete) {
      const timer = setTimeout(() => {
        onAnimationComplete()
      }, animationDuration)

      return () => clearTimeout(timer)
    }
  }, [isClosing, onAnimationComplete])

  const handleClose = () => {
    if (onClose) {
      console.log('Hotspot card close button clicked', 'hotspot')
      onClose()
    }
  }

  return (
    <div className={wrapperClassName}>
      <div className={'absolute top-2 md:top-0 right-4 md:right-0 bottom-0 md:flex md:items-center'}>
        <Button small onClick={handleClose} className="!p-[16px] relative z-50 md:left-[50%]">
          <Close />
        </Button>
      </div>

      <div className={'overflow-y-auto absolute top-0 left-0'}>
        <div className={'relative flex justify-between flex-col p-4 md:p-[40px] space-y-6 md:mt-[-30px]'}>
          <div className="md:py-16">
            <h2 className={'font-domino text-white font-bold text-4xl mb-6 max-w-[calc(100%-64px)] md:max-w-none'}>
              {copyHelper(data.title, data.ma_title)}
            </h2>

            <div className={'font-roboto text-sm md:text-base text-white flex '}>
              <p className={'text-pretty'}>{mainCopy}</p>
              {secondaryCopy && <p className={'ml-6 text-pretty'}>{secondaryCopy}</p>}
            </div>
          </div>

          {data.image && (
            <div>
              <img src={data.image} alt={data.title} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export { HotspotCard }
